declare global {
    /** @internal */
    interface __PNGlobal__ {
        ENV: 'development' | 'production'
        CDN_DOMAIN: string
        KAPI_DOMAIN: string
        LIBRARY_NAME: string
        LIBRARY_VERSION: string
        CORE_NAME: string
        CORE_VERSION: string
        PUBLIC_NS: string
    }

    /** @internal */
    const __PNGlobal__: __PNGlobal__
}

/** @internal */
export class Globals {
    // region Injected Globals

    static get _env() {
        return __PNGlobal__.ENV
    }
    static get _libraryName() {
        return __PNGlobal__.LIBRARY_NAME
    }
    static get _libraryVersion() {
        return __PNGlobal__.LIBRARY_VERSION
    }
    static get _coreName() {
        return __PNGlobal__.CORE_NAME
    }
    static get _coreVersion() {
        return __PNGlobal__.CORE_VERSION
    }
    static get _cdnDomain() {
        return __PNGlobal__.CDN_DOMAIN
    }
    static get _kApiDomain() {
        return __PNGlobal__.KAPI_DOMAIN
    }
    static get _publicNamespace() {
        return __PNGlobal__.PUBLIC_NS
    }
    static get _kApiEventsUrl() {
        return `https://${this._kApiDomain}/event-stream`
    }
    static get _kApiAllowUrl() {
        return `https://${this._kApiDomain}/allow`
    }
    static get _cdnDomainSettingsUrl() {
        return `https://${this._cdnDomain}/domain-settings`
    }

    static get _isDev() {
        return this._env === 'development'
    }
    static get _isProd() {
        return this._env === 'production'
    }
    // endregion

    // region Flags

    static get _sdkHasEventsOnlyDomainsFlag() {
        return 'FEAT_SDK_EVENT_ONLY_DOMAINS' as const
    }

    static get _sdkHasForceDebugLogsEnabledFlag() {
        return 'FEAT_WEB_FORCE_DEBUG_LOGS' as const
    }

    static get _sdkDebugEventsEnabledFlag() {
        return 'SDK_DEBUG_EVENTS' as const
    }

    // endregion

    // region Storage
    // region Keys
    /**
     * ! NOTE:
     * Storage keys CANNOT be changed without severely impacting the client
     * userbase and any changes should be discussed thoroughly and a proper
     * migration strategy should be planned before any changes are deployed
     */
    static get _pnCookieStorageKey() {
        return '_pn' as const
    }
    static get _pnCookieStorageKeyRegex() {
        return new RegExp(`^${this._pnCookieStorageKey}(_[0-9A-Za-z]{8})?=`)
    }
    static get _pnCookieStorageLegacyKeyPrefix() {
        return '_pn' as const
    }
    static get _pnCookieStorageLegacyKeyPrefixRegex() {
        return new RegExp(`^((${this._pnCookieStorageLegacyKeyPrefix}.+)|(pushly.+))`)
    }
    static get _pnStorageTypeLuaKey() {
        return 'lua' as const
    }
    static get _pnStorageUpdatedAtKey() {
        return 'ts' as const
    }

    static get _pnSessionStorageKey() {
        return '_ps' as const
    }

    /**
     * sessionStorage key holding the domain settings document, kept separate from the key the
     * session manager writes.
     *
     * They shared one blob until the settings document was merged, at which point a ~15KB
     * payload landed in the same value a one-second session timer re-parses and re-serialises
     * on every tick. Splitting them keeps that timer's cost proportional to the session
     * counters it actually maintains.
     */
    static get _pnSettingsSessionStorageKey() {
        return '_pnset' as const
    }
    // endregion
    static get _pnCookieStorageMaxDays() {
        return 180 as const
    }
    // endregion

    // region Cache
    /**
     * Freshness window for the merged domain settings document.
     *
     * 300s matches the SDK bundle this document complements, and is the default the
     * comparable feature-flag SDKs converged on (Optimizely polls its datafile every 5
     * minutes by default). The previous 60s was five times more aggressive than that with no
     * recorded justification, and bought nothing: the overwhelming majority of these
     * responses are 304s, so the cost was a request per client per minute to learn that
     * nothing had changed.
     *
     * Publisher preview is served by {@link _pnPreviewUrlSearchParameter} rather than by a
     * short TTL, so this value is not constrained by anyone's testing loop.
     */
    static get _pnDomainSettingsCacheTtlSeconds() {
        return 300 as const
    }

    /**
     * How long past its freshness window a stored settings document may still be used while
     * a replacement is fetched in the background.
     *
     * Without this, the first load after the TTL expires blocks on the network before the
     * SDK can do anything. Within this window that load is served from storage immediately
     * and the refresh lands for the next one, so expiry costs latency on no page view.
     */
    static get _pnDomainSettingsStaleWhileRevalidateSeconds() {
        return 86_400 as const
    }

    // endregion

    // region Preview
    /**
     * Opts the current page out of the stored settings copy so a publisher editing their
     * configuration sees it immediately while testing on their own site.
     *
     * This is what decouples the production TTL from the publisher's verification loop: the
     * feedback loop that a short TTL used to provide is served here instead, for the one
     * session that needs it, rather than by refetching for every end user. Preview is
     * deliberately opt-in per page load and never persisted.
     */
    static get _pnPreviewUrlSearchParameter() {
        return 'pn_preview' as const
    }

    /**
     * Cookie equivalent of {@link _pnPreviewUrlSearchParameter}, for verifying a flow that
     * navigates across pages where carrying a query parameter through every link is not
     * practical. Any non-empty value other than `0`/`false` enables preview.
     */
    static get _pnPreviewCookieName() {
        return '_pn_preview' as const
    }
    // endregion

    // region Events
    static get _eventQueueMaxSize() {
        return 100 as const
    }
    static get _eventQueueFlushIntervalMS() {
        return 1000 as const
    }
    /**
     * Send attempts allowed per event, counting the first. Also the default attempt budget for a
     * {@link Request}, so it bounds both the per-request retry loop and the queue's own per-event
     * budget across flushes.
     *
     * Kept small on purpose: the flush timer re-attempts regardless, so a long inner retry chain
     * only multiplies load against a service that is already struggling. Must equal
     * `MAX_ATTEMPTS` in `request/backoff.ts`.
     */
    static get _eventMaxSendAttempts() {
        return 4 as const
    }
    static get _eventQueueFlushTriggerSize() {
        return 10 as const
    }
    static get _tevChannel() {
        return 'WEB' as const
    }
    static get _tevVapidProvider() {
        return 'VAPID' as const
    }

    static get _tevFieldErrorType() {
        return 'error_type' as const
    }
    static get _tevFieldErrorMessage() {
        return 'error_message' as const
    }
    static get _tevFieldErrorBacktrace() {
        return 'backtrace' as const
    }
    static get _tevFieldErrorContext() {
        return 'context' as const
    }
    // endregion
}
